import { ArrowIcon, DecreaseIcon, IncreaseIcon } from "assets/icons";
import cn from "classnames";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Progress } from "ui";

import { RevenueByCategory, RevenueByVariant } from "../../../../apis";
import {
  barPercent,
  formatXof,
  getRowMetricDelta,
  shareOf
} from "../../financialFormat";
import { CategoryMetric } from "./CategoryBreakdown.types";
import ProductRow from "./ProductRow";

import styles from "./CategoryBreakdown.module.scss";

interface CategoryRowProps {
  node: RevenueByCategory;
  variants: RevenueByVariant[];
  metric: CategoryMetric;
  totalValue: number;
  showComparison: boolean;
  shareLabel: string;
  productsLabel: string;
  renderProductExtra?: (
    variant: RevenueByVariant,
    levelTotal: number
  ) => ReactNode;
  renderProductBadge?: (variant: RevenueByVariant) => ReactNode;
}

const CategoryRow = ({
  node,
  variants,
  metric,
  totalValue,
  showComparison,
  shareLabel,
  productsLabel,
  renderProductExtra,
  renderProductBadge
}: CategoryRowProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const value = node[metric];
  const share = shareOf(value, totalValue);
  const delta = getRowMetricDelta(node, metric);
  const isNegativeDelta = typeof delta === "number" && delta < 0;

  const totalUnits = variants.reduce((acc, variant) => acc + variant.units, 0);
  const isExpandable = variants.length > 0;

  return (
    <div
      className={cn(styles.categoryRow, { [styles.categoryRow__open]: isOpen })}
    >
      <button
        type="button"
        className={styles.categoryRow__header}
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={!isExpandable}
      >
        <span
          className={cn(styles.categoryRow__toggle, {
            [styles["categoryRow__toggle--open"]]: isOpen,
            [styles["categoryRow__toggle--disabled"]]: !isExpandable
          })}
        >
          <Icon as={ArrowIcon} boxSize={20} />
        </span>

        <span className={styles.categoryRow__titles}>
          <span className={styles.categoryRow__name}>
            {node.categoryName ?? t("Uncategorised")}
          </span>
          <span className={styles.categoryRow__hint}>
            {isExpandable
              ? `${variants.length} ${t(variants.length === 1 ? "product" : "products")}`
              : t("No products")}
          </span>
        </span>

        <span className={styles.categoryRow__amount}>{formatXof(value)}</span>

        <span className={styles.categoryRow__meta}>
          <span className={styles.categoryRow__share}>
            {share === null ? (
              "—"
            ) : (
              <>
                <strong>{share.toFixed(1)}%</strong> {t(shareLabel)}
              </>
            )}
          </span>
          {showComparison && (
            <span className={styles.categoryRow__delta}>
              {delta === null ? (
                <span
                  className={cn(
                    styles.categoryRow__deltaValue,
                    styles["categoryRow__deltaValue--neutral"]
                  )}
                >
                  —
                </span>
              ) : (
                <>
                  <Icon
                    as={isNegativeDelta ? DecreaseIcon : IncreaseIcon}
                    color={isNegativeDelta ? "#FF0033" : "#16C350"}
                  />
                  <span
                    className={cn(styles.categoryRow__deltaValue, {
                      [styles["categoryRow__deltaValue--negative"]]:
                        isNegativeDelta
                    })}
                  >
                    {Math.abs(delta).toFixed(1)}%
                  </span>
                </>
              )}
            </span>
          )}
        </span>

        <span className={styles.categoryRow__bar}>
          <Progress percentage={barPercent(share)} transparentBarBackground />
        </span>
      </button>

      {isOpen && (
        <div className={styles.categoryRow__products}>
          <div className={styles.categoryRow__productsHeader}>
            <span>
              {t(productsLabel)} · {variants.length}
            </span>
            <span>
              {totalUnits} {t("units")} · {formatXof(value)}
            </span>
          </div>

          {variants.map((variant) => (
            <ProductRow
              key={variant.variantId ?? variant.variantName}
              variant={variant}
              metric={metric}
              levelTotal={value}
              renderProductExtra={renderProductExtra}
              renderProductBadge={renderProductBadge}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryRow;
